import React from "react";
import { createPortal } from "react-dom";
import { usePlayer } from "../context/PlayerContext";
import { Play, Pause, SkipBack, SkipForward, Square, Volume2, VolumeX, Music2, Radio, Maximize2, Minimize2, X, Eye, ThumbsUp, ThumbsDown } from "lucide-react";
import { coverUrl } from "../lib/api";
import FrequencyControl from "./FrequencyControl";

function formatTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

const DEFAULT_COVER = "https://images.unsplash.com/photo-1771747131849-41056d3d9be3?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";

export default function PersistentPlayer() {
  const {
    currentTrack, isPlaying, currentTime, duration, volume, togglePlay, next, prev, seek, setVolume, mode,
    isVideo, hasCoverVideo, expanded, expand, collapse, videoHidden, hideVisual, showVisual,
    playbackError, dismissError, needsTapToStart,
    stationName, reactions, thumbUp, thumbDown, stopPlayback,
    attachAudio, attachAudioB, attachVideo, attachCover,
    videoHost,
  } = usePlayer();

  const cover = currentTrack ? (coverUrl(currentTrack) || DEFAULT_COVER) : DEFAULT_COVER;
  const visualVisible = !videoHidden && (isVideo || hasCoverVideo);
  const showStaticCover = !visualVisible;
  const myReaction = currentTrack ? reactions[currentTrack.id] : null;

  // Always-on portal so the <video> element identity is preserved across host changes.
  // External (e.g. NowPlayingHero) → falls back to the default host below.
  const defaultVideoHostRef = React.useRef(null);
  const [portalTarget, setPortalTarget] = React.useState(null);
  React.useEffect(() => {
    setPortalTarget(videoHost || defaultVideoHostRef.current);
  }, [videoHost]);
  // After first paint, populate defaultVideoHostRef so the portal can mount initially.
  React.useEffect(() => {
    if (!portalTarget && defaultVideoHostRef.current) {
      setPortalTarget(defaultVideoHostRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // Escape key collapses the expanded video view (standard fullscreen-close UX)
  React.useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        collapse();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, collapse]);

  // Main video classes — single element, position changes based on expanded; offscreen if hidden
  const mainClass = (isVideo && !videoHidden)
    ? (expanded
        ? "fixed left-0 right-0 bottom-24 z-50 mx-auto max-w-4xl w-full aspect-video bg-black border-t border-b border-[#C5A059]/30 shadow-2xl"
        : "absolute inset-0 w-full h-full object-cover bg-black")
    : "absolute left-[-9999px] top-[-9999px] w-px h-px opacity-0 pointer-events-none";

  return (
    <div data-testid="persistent-player" className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#060709]/85 border-t border-[#C5A059]/15">
      {playbackError && (
        mode === "radio" ? (
          <div className="bg-[#C5A059]/10 border-b border-[#C5A059]/30 px-4 md:px-8 py-2 flex items-center justify-between gap-3" data-testid="player-reconnect-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-block w-2 h-2 rounded-full bg-[#C5A059] animate-pulse flex-shrink-0" />
              <div className="text-xs text-[#FDFBF7] truncate">Reconnecting to live radio…</div>
            </div>
            <button onClick={dismissError} className="text-xs text-[#A1A4AB] hover:text-[#FDFBF7] flex-shrink-0" data-testid="player-reconnect-dismiss">×</button>
          </div>
        ) : (
          <div className="bg-red-500/10 border-b border-red-500/30 px-4 md:px-8 py-2 flex items-center justify-between gap-3" data-testid="player-error-banner">
            <div className="text-xs text-red-300 truncate">{playbackError}</div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={next} className="text-xs overline px-3 py-1 rounded-full border border-red-400/30 text-red-200 hover:bg-red-500/10" data-testid="player-error-skip">Skip</button>
              <button onClick={dismissError} className="text-xs text-red-300 hover:text-red-100" data-testid="player-error-dismiss">×</button>
            </div>
          </div>
        )
      )}

      {needsTapToStart && currentTrack && (
        <div className="bg-[#C5A059]/10 border-b border-[#C5A059]/30 px-4 md:px-8 py-2 flex items-center justify-between gap-3" data-testid="player-tap-banner">
          <div className="text-xs text-[#FDFBF7]">Tap play to start. Browsers require a tap before audio can play.</div>
          <button onClick={togglePlay} className="text-xs overline px-3 py-1 rounded-full bg-[#C5A059] text-[#060709] hover:bg-[#DCC286]" data-testid="player-tap-start">Play</button>
        </div>
      )}

      {/* Close + Hide buttons overlaid on the expanded video itself — anchored to the video's bbox so they're ALWAYS visible regardless of viewport size. */}
      {isVideo && expanded && !videoHidden && (
        <div
          className="fixed left-0 right-0 bottom-24 z-[60] mx-auto max-w-4xl w-full aspect-video pointer-events-none"
        >
          <div className="absolute top-2 right-2 flex items-center gap-2 pointer-events-auto">
            <button
              onClick={hideVisual}
              data-testid="player-hide-visual-btn-expanded"
              title="Hide video (audio keeps playing)"
              aria-label="Hide video, keep audio playing"
              className="h-9 px-3 rounded-full bg-black/85 hover:bg-black border border-[#C5A059]/40 hover:border-[#C5A059] text-[#FDFBF7] text-xs overline flex items-center gap-1.5 backdrop-blur-sm shadow-lg transition-colors"
            >
              <Minimize2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Hide
            </button>
            <button
              onClick={collapse}
              data-testid="player-close-expanded-btn"
              title="Close (Esc)"
              aria-label="Close expanded video"
              className="w-10 h-10 rounded-full bg-black/90 hover:bg-[#C5A059] border-2 border-[#C5A059] text-[#FDFBF7] hover:text-[#060709] flex items-center justify-center backdrop-blur-sm shadow-xl transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}

      <div className="h-24 px-4 md:px-8 flex items-center gap-3 md:gap-6">
        {/* Now playing (left) — cover slot */}
        <div className="flex items-center gap-3 min-w-0 w-[34%] md:w-[26%]">
          <div className="relative w-14 h-14 rounded-md overflow-hidden border border-[#22262E] bg-[#111317] flex-shrink-0">
            {/* Static cover image (audio-only, no animated cover) */}
            {currentTrack && showStaticCover && (
              <img src={cover} alt="" className="w-full h-full object-cover" />
            )}
            {!currentTrack && (
              <div className="w-full h-full flex items-center justify-center text-[#6C707A]">
                <Music2 className="w-6 h-6" strokeWidth={1.5} />
              </div>
            )}

            {/* Animated cover MP4 — muted/looped, only when audio track has cover_video and not hidden */}
            <video
              ref={attachCover}
              muted
              loop
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              data-testid="player-cover-video"
              className={
                hasCoverVideo && !videoHidden
                  ? "absolute inset-0 w-full h-full object-cover"
                  : "absolute left-[-9999px] top-[-9999px] w-px h-px opacity-0 pointer-events-none"
              }
            />

            {/* Default inline portal target — fills the player thumbnail slot when no external host claims the video */}
            <div ref={(el) => { defaultVideoHostRef.current = el; }} className="absolute inset-0" data-testid="player-video-default-host" />

            {/* Single main <video> DOM node, always rendered through a portal so the SAME element
                can teleport between this default host and an external host (e.g. the /stations hero)
                without ever unmounting — preserves playback state, src, currentTime, audio decoding. */}
            {portalTarget && createPortal(
              <video
                ref={attachVideo}
                playsInline
                preload="metadata"
                crossOrigin="anonymous"
                data-testid="player-main-media"
                className={
                  videoHost && isVideo
                    ? "absolute inset-0 w-full h-full object-cover bg-black"
                    : mainClass
                }
                controls={isVideo && expanded && !videoHost}
              />,
              portalTarget,
            )}

            {/* Hidden audio elements — DUAL DECK so the Phase B.1 crossfade has
                two independent media sources to alternate between. Element A is the
                primary deck; element B is used during a fade. Both stay in the DOM
                (not display:none) so iOS Safari treats them as normal audio elements
                eligible for background playback. */}
            <audio
              ref={attachAudio}
              preload="metadata"
              crossOrigin="anonymous"
              data-testid="player-audio-element"
              className="absolute left-[-9999px] top-[-9999px] w-px h-px opacity-0 pointer-events-none"
            />
            <audio
              ref={attachAudioB}
              preload="metadata"
              crossOrigin="anonymous"
              data-testid="player-audio-element-b"
              className="absolute left-[-9999px] top-[-9999px] w-px h-px opacity-0 pointer-events-none"
            />

            {mode === "radio" && currentTrack && !currentTrack.is_commercial && (
              <div className="absolute top-0.5 left-0.5 bg-[#C5A059] text-[#060709] text-[9px] font-bold px-1 rounded-sm flex items-center gap-0.5 z-10">
                <Radio className="w-2.5 h-2.5" strokeWidth={2} /> LIVE
              </div>
            )}
            {currentTrack?.is_commercial && (
              <div
                className="absolute top-0.5 left-0.5 bg-[#FDFBF7] text-[#060709] text-[9px] font-bold px-1 rounded-sm z-10"
                data-testid="player-sponsor-badge"
                title="Sponsor message"
              >
                SPONSOR
              </div>
            )}
            {currentTrack?.is_prayer && (
              <div
                className="absolute top-0.5 left-0.5 bg-[#C5A059] text-[#060709] text-[9px] font-bold px-1 rounded-sm z-10"
                data-testid="player-prayer-badge"
                title="Scheduled prayer slot"
              >
                PRAYER
              </div>
            )}
            {currentTrack?.is_shofar && (
              <div
                className="absolute top-0.5 left-0.5 bg-[#C5A059] text-[#060709] text-[9px] font-bold px-1 rounded-sm z-10 animate-pulse"
                data-testid="player-shofar-badge"
                title="Shofar broadcast"
              >
                SHOFAR
              </div>
            )}

            {/* Hide-video X button — appears when a live visual is showing in the cover slot (but not when expanded; expanded has its own minimize) */}
            {visualVisible && !expanded && (
              <button
                onClick={hideVisual}
                data-testid="player-hide-visual-btn"
                title="Hide video"
                aria-label="Hide video"
                className="absolute top-0.5 right-0.5 z-20 w-5 h-5 rounded-full bg-[#060709]/80 border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#060709] hover:border-[#C5A059] flex items-center justify-center"
              >
                <X className="w-3 h-3" strokeWidth={2} />
              </button>
            )}
          </div>
          <div className="min-w-0 hidden sm:block">
            <div className="text-[#FDFBF7] text-sm font-medium truncate" data-testid="player-track-title">
              {currentTrack ? currentTrack.title : "Nothing playing"}
            </div>
            <div className="text-[#A1A4AB] text-xs truncate flex items-center gap-2" data-testid="player-track-artist">
              <span className="truncate">
                {currentTrack ? currentTrack.artist : "Select a track to begin"}
                {stationName && currentTrack && <span className="text-[#6C707A]"> · {stationName}</span>}
              </span>
              {videoHidden && (isVideo || hasCoverVideo) && (
                <button
                  onClick={showVisual}
                  data-testid="player-show-visual-btn"
                  className="overline text-[10px] px-2 py-0.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 flex items-center gap-1 flex-shrink-0"
                  title="Show video"
                >
                  <Eye className="w-3 h-3" strokeWidth={1.5} /> Show video
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Controls (center) */}
        <div className="flex-1 flex flex-col items-center gap-1.5">
          <div className="flex items-center gap-4">
            <button onClick={prev} data-testid="player-prev-btn" disabled={!currentTrack} className="text-[#A1A4AB] hover:text-[#FDFBF7] disabled:opacity-30 transition-colors">
              <SkipBack className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={togglePlay}
              data-testid="player-play-toggle"
              disabled={!currentTrack}
              className="w-11 h-11 rounded-full bg-[#C5A059] text-[#060709] flex items-center justify-center hover:bg-[#DCC286] hover:scale-105 transition-all disabled:opacity-30 disabled:hover:scale-100 shadow-[0_0_20px_rgba(197,160,89,0.3)]"
            >
              {isPlaying ? <Pause className="w-5 h-5" strokeWidth={2} /> : <Play className="w-5 h-5 ml-0.5" strokeWidth={2} />}
            </button>
            <button
              onClick={stopPlayback}
              data-testid="player-stop-btn"
              disabled={!currentTrack}
              title="Stop (resets the queue)"
              aria-label="Stop"
              className="w-9 h-9 rounded-full border border-[#22262E] text-[#A1A4AB] hover:text-red-300 hover:border-red-400/40 hover:bg-red-500/5 disabled:opacity-30 transition-colors flex items-center justify-center"
            >
              <Square className="w-3.5 h-3.5" strokeWidth={2} fill="currentColor" />
            </button>
            <button onClick={next} data-testid="player-next-btn" disabled={!currentTrack} className="text-[#A1A4AB] hover:text-[#FDFBF7] disabled:opacity-30 transition-colors">
              <SkipForward className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="w-full max-w-xl hidden md:flex items-center gap-2">
            <span className="text-[10px] text-[#6C707A] w-8 text-right tabular-nums">{formatTime(currentTime)}</span>
            <input
              type="range" min="0" max={duration || 0} step="0.1"
              value={currentTime || 0}
              onChange={(e) => seek(parseFloat(e.target.value))}
              className="gold-range flex-1"
              data-testid="player-seek-bar"
              disabled={!currentTrack}
            />
            <span className="text-[10px] text-[#6C707A] w-8 tabular-nums">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Right: thumbs + expand + volume */}
        <div className="hidden md:flex items-center gap-2 w-[24%] justify-end">
          {currentTrack && (
            <div className="flex items-center gap-1 mr-1">
              <button
                onClick={thumbUp}
                data-testid="player-thumb-up"
                title="Thumbs up"
                className={`p-1.5 rounded-full transition-colors ${myReaction === "like" ? "text-[#C5A059] bg-[#C5A059]/10" : "text-[#A1A4AB] hover:text-[#FDFBF7]"}`}
              >
                <ThumbsUp className="w-4 h-4" strokeWidth={1.5} fill={myReaction === "like" ? "#C5A059" : "none"} />
              </button>
              <button
                onClick={thumbDown}
                data-testid="player-thumb-down"
                title="Thumbs down — skip and reduce"
                className={`p-1.5 rounded-full transition-colors ${myReaction === "dislike" ? "text-red-300 bg-red-500/10" : "text-[#A1A4AB] hover:text-red-300"}`}
              >
                <ThumbsDown className="w-4 h-4" strokeWidth={1.5} fill={myReaction === "dislike" ? "rgb(252,165,165)" : "none"} />
              </button>
            </div>
          )}
          {isVideo && !expanded && !videoHidden && (
            <button
              onClick={expand}
              data-testid="player-expand-video-btn"
              className="text-xs overline px-3 py-1.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 flex items-center gap-1.5"
              title="Expand video"
            >
              <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Expand
            </button>
          )}
          <FrequencyControl />
          <button onClick={() => setVolume(volume > 0 ? 0 : 0.85)} data-testid="player-volume-toggle" className="text-[#A1A4AB] hover:text-[#FDFBF7]">
            {volume === 0 ? <VolumeX className="w-4 h-4" strokeWidth={1.5} /> : <Volume2 className="w-4 h-4" strokeWidth={1.5} />}
          </button>
          <input type="range" min="0" max="1" step="0.01" value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="gold-range w-28" data-testid="player-volume-slider" />
        </div>

        {/* Mobile: small expand button + frequency control */}
        <div className="md:hidden flex items-center gap-1.5">
          {isVideo && !expanded && !videoHidden && (
            <button onClick={expand} data-testid="player-expand-video-btn-mobile" className="p-2 rounded-full border border-[#C5A059]/40 text-[#C5A059]" aria-label="Expand video">
              <Maximize2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
          <FrequencyControl />
        </div>
      </div>
    </div>
  );
}
